import React, { useState, useEffect, useCallback, useRef } from 'react';
import styled from 'styled-components';
import api from '../api';
import { useAuth } from '../context/auth';
import { LoadingText } from '../components/Style';
import { DisplayPost } from '../components/DisplayPost';
import PlayAllButton from '../components/PlayAllButton';

var FeedContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1em;
  align-items: center;
  max-width: 410px;
  margin: 0 auto;
  padding: 1em 0.75em 0;
`;

var EmptyMessage = styled.p`
  text-align: center;
  color: #999;
  margin-top: 2rem;
  font-size: 0.9rem;
`;

var TabRow = styled.div`
  display: flex;
  align-items: center;
  width: 100%;
  border-bottom: 1px solid #eee;
  margin-bottom: -0.35em;
  padding: 0 0.5rem;
  box-sizing: border-box;
`;

var TabTitle = styled.div`
  flex: 1;
  font-size: 0.85rem;
  font-weight: 700;
  align-self: center;
  margin-top: 2px;
`;

var SortWrapper = styled.div`
  position: relative;
  flex-shrink: 0;
`;

var SortButton = styled.button`
  background: white;
  border: 1px solid #ddd;
  border-radius: 8px;
  padding: 0.3rem 1.4rem 0.3rem 0.6rem;
  font-size: 0.72rem;
  font-weight: 600;
  font-family: inherit;
  color: #333;
  cursor: pointer;
  position: relative;

  &::after {
    content: '';
    position: absolute;
    right: 0.45rem;
    top: 50%;
    transform: translateY(-50%);
    border-left: 3.5px solid transparent;
    border-right: 3.5px solid transparent;
    border-top: 4px solid #999;
  }
`;

var SortMenu = styled.div`
  position: absolute;
  top: calc(100% + 4px);
  right: 0;
  background: white;
  border: 1px solid #ddd;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.12);
  z-index: 9999;
  min-width: 120px;
  overflow: hidden;
`;

var SortOption = styled.button`
  display: block;
  width: 100%;
  background: ${function (props) {
    return props.$active ? '#f5f5f5' : 'white';
  }};
  border: none;
  padding: 0.5rem 0.75rem;
  font-size: 0.75rem;
  font-weight: ${function (props) {
    return props.$active ? '700' : '400';
  }};
  font-family: inherit;
  color: #333;
  cursor: pointer;
  text-align: left;

  &:hover {
    background: #f0f0f0;
  }
`;

var FilterIconButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  padding: 0.5rem;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  align-self: stretch;
`;

var FilterDot = styled.span`
  position: absolute;
  top: 50%;
  right: 2px;
  margin-top: -8px;
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: #000;
`;

var FilterBar = styled.div`
  display: flex;
  align-items: center;
  width: 100%;
  padding: 0.5rem 0.5rem;
  margin-bottom: -0.35em;
  font-size: 0.85rem;
  color: #000;
  font-weight: 700;
  border-bottom: 1px solid #eee;
  box-sizing: border-box;
`;

var FilterBarText = styled.span`
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

var FilterBarClear = styled.button`
  background: white;
  border: 1px solid #ccc;
  border-radius: 4px;
  cursor: pointer;
  font-size: 0.72rem;
  color: #333;
  font-weight: 700;
  padding: 0.2rem 0.5rem;
  flex-shrink: 0;
  margin-left: 0.5rem;
  font-family: inherit;

  &:hover {
    background: #eee;
  }
`;

// --- Bottom sheet (same as feed) ---

var SheetWrapper = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 200;
  display: flex;
  align-items: flex-end;
  justify-content: center;
`;

var SheetBackdrop = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.4);
  animation: fadeIn 0.2s ease-out;
  @keyframes fadeIn {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }
`;

var SheetContainer = styled.div`
  position: relative;
  width: 100%;
  max-width: 400px;
  background: white;
  border-radius: 16px 16px 0 0;
  max-height: 65vh;
  overflow-y: auto;
  overscroll-behavior: contain;
  padding: 0 1.25rem calc(1.25rem + 60px);
  -webkit-overflow-scrolling: touch;
  touch-action: pan-y;
  scrollbar-width: none;
  &::-webkit-scrollbar {
    display: none;
  }
  animation: slideUp 0.25s ease-out;
  @keyframes slideUp {
    from {
      transform: translateY(100%);
    }
    to {
      transform: translateY(0);
    }
  }
`;

var DragHandleArea = styled.div`
  padding: 0.75rem 0 0.5rem;
  cursor: grab;
  touch-action: none;
`;

var DragHandleBar = styled.div`
  width: 36px;
  height: 4px;
  border-radius: 2px;
  background: #ddd;
  margin: 0 auto;
`;

var SheetHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin: 0 0 1rem;
`;

var SheetTitle = styled.h3`
  font-size: 1rem;
  font-weight: 700;
  margin: 0;
`;

var SheetDoneButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  font-size: 0.85rem;
  font-weight: 600;
  color: #000;
  padding: 0.25rem 0;
`;

var SheetSectionLabel = styled.p`
  font-size: 0.7rem;
  font-weight: 700;
  color: #999;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  margin: 1rem 0 0.5rem;
`;

var SheetPillGrid = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.4rem;
`;

var SheetPill = styled.button`
  padding: 0.35rem 0.75rem;
  border-radius: 999px;
  border: 1px solid ${function (props) {
      return props.$selected ? '#000' : '#ddd';
    }};
  background: ${function (props) {
    return props.$selected ? '#000' : 'white';
  }};
  color: ${function (props) {
    return props.$selected ? 'white' : '#333';
  }};
  font-size: 0.75rem;
  font-weight: ${function (props) {
    return props.$selected ? '700' : '400';
  }};
  cursor: pointer;
  white-space: nowrap;
  &:hover {
    border-color: #000;
  }
`;

var SheetClearButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  font-size: 0.85rem;
  color: #999;
  font-weight: 600;
  padding: 0.5rem 0;
  &:hover {
    color: #000;
  }
`;

var SheetResultCount = styled.span`
  font-size: 0.8rem;
  color: #bbb;
  font-weight: 500;
`;

function FilterIcon(props) {
  return (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke={props.active ? '#000' : '#999'}
      strokeWidth="2"
      strokeLinecap="round"
    >
      <line x1="4" y1="6" x2="20" y2="6" />
      <line x1="7" y1="12" x2="17" y2="12" />
      <line x1="10" y1="18" x2="14" y2="18" />
    </svg>
  );
}

function Bookmarks() {
  useEffect(function () {
    document.title = 'Bookmarks | HeavyRotation';
  }, []);

  var authContext = useAuth();
  var authTokens = authContext.authTokens;
  var isLoggedIn = authTokens?.token;

  // Sort
  var _sortMode = useState('recent');
  var sortMode = _sortMode[0];
  var setSortMode = _sortMode[1];

  var _sortMenuOpen = useState(false);
  var sortMenuOpen = _sortMenuOpen[0];
  var setSortMenuOpen = _sortMenuOpen[1];
  var sortMenuRef = useRef(null);

  var _randomSeed = useState(function () {
    return Date.now().toString(36);
  });
  var randomSeed = _randomSeed[0];
  var setRandomSeed = _randomSeed[1];

  function handleSetSort(mode) {
    setSortMode(mode);
    if (mode === 'random') setRandomSeed(Date.now().toString(36));
  }

  // Filters
  var _genres = useState([]);
  var genres = _genres[0];
  var setGenres = _genres[1];

  var _selectedGenres = useState([]);
  var selectedGenres = _selectedGenres[0];
  var setSelectedGenres = _selectedGenres[1];

  var decades = [
    '2020s',
    '2010s',
    '2000s',
    '1990s',
    '1980s',
    '1970s',
    '1960s',
    '1950s',
    '1940s',
    '1930s',
    '1920s',
  ];

  var _selectedDecades = useState([]);
  var selectedDecades = _selectedDecades[0];
  var setSelectedDecades = _selectedDecades[1];

  var _sheetOpen = useState(false);
  var sheetOpen = _sheetOpen[0];
  var setSheetOpen = _sheetOpen[1];

  var _dragY = useState(0);
  var dragY = _dragY[0];
  var setDragY = _dragY[1];
  var dragStartRef = useRef(null);
  var dragYRef = useRef(0);

  var hasFilters = selectedGenres.length > 0 || selectedDecades.length > 0;

  useEffect(function () {
    api
      .get('/api/media/genres')
      .then(function (r) {
        setGenres(r.data || []);
      })
      .catch(function () {});
  }, []);

  // Close sort menu on click outside
  useEffect(
    function () {
      if (!sortMenuOpen) return;
      function handleClick(e) {
        if (sortMenuRef.current && !sortMenuRef.current.contains(e.target)) setSortMenuOpen(false);
      }
      document.addEventListener('mousedown', handleClick);
      return function () {
        document.removeEventListener('mousedown', handleClick);
      };
    },
    [sortMenuOpen]
  );

  function toggleGenre(g) {
    setSelectedGenres(function (prev) {
      return prev.indexOf(g) >= 0
        ? prev.filter(function (x) {
            return x !== g;
          })
        : prev.concat([g]);
    });
  }

  function toggleDecade(d) {
    setSelectedDecades(function (prev) {
      return prev.indexOf(d) >= 0
        ? prev.filter(function (x) {
            return x !== d;
          })
        : prev.concat([d]);
    });
  }

  function decadesToYearPrefixes(decs) {
    return decs.map(function (d) {
      return d.slice(0, 3);
    });
  }

  function clearAllFilters() {
    setSelectedGenres([]);
    setSelectedDecades([]);
  }

  function filterDescription() {
    var parts = [];
    for (var i = 0; i < selectedGenres.length; i++) parts.push(selectedGenres[i]);
    for (var j = 0; j < selectedDecades.length; j++) parts.push(selectedDecades[j]);
    return parts.join(' \u00B7 ');
  }

  function handleDragMove(clientY) {
    if (dragStartRef.current === null) return;
    var delta = clientY - dragStartRef.current;
    if (delta > 0) {
      dragYRef.current = delta;
      setDragY(delta);
    }
  }
  function handleDragEnd() {
    if (dragYRef.current > 100) setSheetOpen(false);
    dragYRef.current = 0;
    setDragY(0);
    dragStartRef.current = null;
  }

  // Fetch bookmarks -- use search endpoint for sort/filter support
  var _allBookmarks = useState([]);
  var allBookmarks = _allBookmarks[0];
  var setAllBookmarks = _allBookmarks[1];

  var _loading = useState(true);
  var loading = _loading[0];
  var setLoading = _loading[1];

  useEffect(function () {
    setLoading(true);
    api
      .get('/api/bookmarks/posts?limit=100')
      .then(function (response) {
        setAllBookmarks(response.data.posts || []);
        setLoading(false);
      })
      .catch(function () {
        setLoading(false);
      });
  }, []);

  // Client-side filter + sort
  var posts = allBookmarks.slice();

  if (selectedGenres.length > 0) {
    posts = posts.filter(function (p) {
      var g = (p.media && p.media.genre) || '';
      return selectedGenres.some(function (sg) {
        return g.toLowerCase().indexOf(sg.toLowerCase()) !== -1;
      });
    });
  }

  if (selectedDecades.length > 0) {
    var prefixes = decadesToYearPrefixes(selectedDecades);
    posts = posts.filter(function (p) {
      var rd = (p.media && p.media.release_date) || '';
      return prefixes.some(function (pre) {
        return rd.indexOf(pre) === 0;
      });
    });
  }

  if (sortMode === 'random') {
    // Seeded shuffle for stability within session
    var seed = randomSeed;
    var rng = function () {
      var x = parseInt(seed, 36) || 1;
      x ^= x << 13;
      x ^= x >> 7;
      x ^= x << 17;
      seed = (x >>> 0).toString(36);
      return (x >>> 0) / 4294967296;
    };
    for (var i = posts.length - 1; i > 0; i--) {
      var j = Math.floor(rng() * (i + 1));
      var temp = posts[i];
      posts[i] = posts[j];
      posts[j] = temp;
    }
  }

  function removePost(postId) {
    setAllBookmarks(function (prev) {
      return prev.filter(function (p) {
        return p.id !== postId;
      });
    });
  }

  // Interaction maps
  var _bookmarkedMap = useState({});
  var bookmarkedMap = _bookmarkedMap[0];
  var setBookmarkedMap = _bookmarkedMap[1];

  useEffect(
    function () {
      var bMap = {};
      for (var i = 0; i < posts.length; i++) bMap[posts[i].id] = true;
      setBookmarkedMap(bMap);
    },
    [posts]
  );

  var _libraryAddMap = useState({});
  var libraryAddMap = _libraryAddMap[0];
  var setLibraryAddMap = _libraryAddMap[1];

  var _userReactionsMap = useState({});
  var userReactionsMap = _userReactionsMap[0];
  var setUserReactionsMap = _userReactionsMap[1];

  var _likedCommentsMap = useState({});
  var likedCommentsMap = _likedCommentsMap[0];
  var setLikedCommentsMap = _likedCommentsMap[1];

  var _followedMap = useState({});
  var followedMap = _followedMap[0];
  var setFollowedMap = _followedMap[1];

  var _repostedMap = useState({});
  var repostedMap = _repostedMap[0];
  var setRepostedMap = _repostedMap[1];

  useEffect(
    function () {
      if (!isLoggedIn) return;
      api
        .get('/api/library-adds')
        .then(function (r) {
          var map = {};
          var ids = r.data || [];
          for (var i = 0; i < ids.length; i++) map[ids[i]] = true;
          setLibraryAddMap(map);
        })
        .catch(function () {});
      api
        .get('/api/follows')
        .then(function (r) {
          var map = {};
          var ids = r.data || [];
          for (var i = 0; i < ids.length; i++) map[ids[i]] = true;
          setFollowedMap(map);
        })
        .catch(function () {});
      api
        .get('/api/post-reactions')
        .then(function (r) {
          var map = {};
          var items = r.data || [];
          for (var i = 0; i < items.length; i++) {
            var pid = items[i].postId;
            if (!map[pid]) map[pid] = [];
            map[pid].push(items[i].emoji);
          }
          setUserReactionsMap(map);
        })
        .catch(function () {});
      api
        .get('/api/comment-likes')
        .then(function (r) {
          var map = {};
          var ids = r.data || [];
          for (var i = 0; i < ids.length; i++) map[ids[i]] = true;
          setLikedCommentsMap(map);
        })
        .catch(function () {});
      api
        .get('/api/reposts')
        .then(function (r) {
          var map = {};
          var ids = r.data || [];
          for (var i = 0; i < ids.length; i++) map[ids[i]] = true;
          setRepostedMap(map);
        })
        .catch(function () {});
    },
    [isLoggedIn]
  );

  function handleBookmarkToggle(postId, bookmarked) {
    setBookmarkedMap(function (prev) {
      var next = {};
      for (var k in prev) {
        if (prev.hasOwnProperty(k)) next[k] = prev[k];
      }
      next[postId] = bookmarked;
      return next;
    });
    if (!bookmarked) removePost(postId);
  }

  function handleReactionToggle(postId, emoji, reacted) {
    setUserReactionsMap(function (prev) {
      var next = {};
      for (var k in prev) {
        if (prev.hasOwnProperty(k)) next[k] = (prev[k] || []).slice();
      }
      if (!next[postId]) next[postId] = [];
      if (reacted) {
        if (next[postId].indexOf(emoji) === -1) next[postId].push(emoji);
      } else {
        next[postId] = next[postId].filter(function (e) {
          return e !== emoji;
        });
      }
      return next;
    });
  }

  function handleFollowToggle(userId, following) {
    setFollowedMap(function (prev) {
      var next = {};
      for (var k in prev) {
        if (prev.hasOwnProperty(k)) next[k] = prev[k];
      }
      next[userId] = following;
      return next;
    });
  }

  function handleRepostToggle(postId, reposted) {
    setRepostedMap(function (prev) {
      var next = {};
      for (var k in prev) {
        if (prev.hasOwnProperty(k)) next[k] = prev[k];
      }
      next[postId] = reposted;
      return next;
    });
  }

  function handleCommentLikeToggle(commentId, liked) {
    setLikedCommentsMap(function (prev) {
      var next = {};
      for (var k in prev) {
        if (prev.hasOwnProperty(k)) next[k] = prev[k];
      }
      next[commentId] = liked;
      return next;
    });
  }

  if (loading) return <LoadingText>Loading...</LoadingText>;

  return (
    <>
      <FeedContainer>
        <TabRow>
          <TabTitle>Bookmarks</TabTitle>
          <SortWrapper ref={sortMenuRef}>
            <SortButton
              onClick={function () {
                setSortMenuOpen(!sortMenuOpen);
              }}
            >
              {sortMode === 'recent' ? 'Recent' : 'Shuffle'}
            </SortButton>
            {sortMenuOpen && (
              <SortMenu>
                {['recent', 'random'].map(function (mode) {
                  var label = mode === 'recent' ? 'Recent' : 'Shuffle';
                  return (
                    <SortOption
                      key={mode}
                      $active={sortMode === mode}
                      onClick={function () {
                        handleSetSort(mode);
                        setSortMenuOpen(false);
                      }}
                    >
                      {label}
                    </SortOption>
                  );
                })}
              </SortMenu>
            )}
          </SortWrapper>
          <FilterIconButton
            onClick={function () {
              setSheetOpen(true);
            }}
          >
            <FilterIcon active={hasFilters} />
            {hasFilters && <FilterDot />}
          </FilterIconButton>
          <PlayAllButton posts={posts} loading={loading} source="bookmarks" />
        </TabRow>

        {hasFilters && (
          <FilterBar>
            <FilterBarText>Filtered by: {filterDescription()}</FilterBarText>
            <FilterBarClear onClick={clearAllFilters}>Clear</FilterBarClear>
          </FilterBar>
        )}

        {posts.length === 0 ? (
          <EmptyMessage>
            {hasFilters ? 'No bookmarks match your filters' : 'No bookmarked posts yet.'}
          </EmptyMessage>
        ) : (
          <>
            {posts.map(function (post) {
              return (
                <DisplayPost
                  post={post}
                  key={post.repostId || post.id}
                  isBookmarked={!!bookmarkedMap[post.id]}
                  userReactions={userReactionsMap[post.id] || []}
                  isAddedToLibrary={!!libraryAddMap[post.id]}
                  isFollowing={!!followedMap[post.user.id]}
                  isReposted={!!repostedMap[post.id]}
                  onBookmarkToggle={handleBookmarkToggle}
                  onReactionToggle={handleReactionToggle}
                  onFollowToggle={handleFollowToggle}
                  followSource="bookmarks"
                  likedCommentsMap={likedCommentsMap}
                  onCommentLikeToggle={handleCommentLikeToggle}
                  onRepostToggle={handleRepostToggle}
                />
              );
            })}
          </>
        )}
      </FeedContainer>

      {sheetOpen && (
        <SheetWrapper>
          <SheetBackdrop
            onClick={function () {
              setSheetOpen(false);
            }}
            style={{ opacity: dragY > 0 ? Math.max(0, 1 - dragY / 300) : 1 }}
          />
          <SheetContainer style={{ transform: dragY > 0 ? 'translateY(' + dragY + 'px)' : 'none' }}>
            <DragHandleArea
              onTouchStart={function (e) {
                dragStartRef.current = e.touches[0].clientY;
              }}
              onTouchMove={function (e) {
                handleDragMove(e.touches[0].clientY);
              }}
              onTouchEnd={function () {
                handleDragEnd();
              }}
              onMouseDown={function (e) {
                dragStartRef.current = e.clientY;
                var onMove = function (ev) {
                  handleDragMove(ev.clientY);
                };
                var onUp = function () {
                  window.removeEventListener('mousemove', onMove);
                  window.removeEventListener('mouseup', onUp);
                  handleDragEnd();
                };
                window.addEventListener('mousemove', onMove);
                window.addEventListener('mouseup', onUp);
              }}
            >
              <DragHandleBar />
            </DragHandleArea>
            <SheetHeader>
              <SheetTitle>
                Filter{' '}
                <SheetResultCount>{loading ? '' : posts.length + ' bookmarks'}</SheetResultCount>
              </SheetTitle>
              <div style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
                {hasFilters && <SheetClearButton onClick={clearAllFilters}>Clear</SheetClearButton>}
                <SheetDoneButton
                  onClick={function () {
                    setSheetOpen(false);
                  }}
                >
                  Done
                </SheetDoneButton>
              </div>
            </SheetHeader>
            <SheetSectionLabel>Genre</SheetSectionLabel>
            <SheetPillGrid>
              <SheetPill
                $selected={selectedGenres.length === 0}
                onClick={function () {
                  setSelectedGenres([]);
                }}
              >
                All
              </SheetPill>
              {genres.map(function (g) {
                return (
                  <SheetPill
                    key={g}
                    $selected={selectedGenres.indexOf(g) >= 0}
                    onClick={function () {
                      toggleGenre(g);
                    }}
                  >
                    {g}
                  </SheetPill>
                );
              })}
            </SheetPillGrid>
            <SheetSectionLabel>Year</SheetSectionLabel>
            <SheetPillGrid>
              <SheetPill
                $selected={selectedDecades.length === 0}
                onClick={function () {
                  setSelectedDecades([]);
                }}
              >
                All
              </SheetPill>
              {decades.map(function (d) {
                return (
                  <SheetPill
                    key={d}
                    $selected={selectedDecades.indexOf(d) >= 0}
                    onClick={function () {
                      toggleDecade(d);
                    }}
                  >
                    {d}
                  </SheetPill>
                );
              })}
            </SheetPillGrid>
          </SheetContainer>
        </SheetWrapper>
      )}
    </>
  );
}

export default Bookmarks;
